<template>
  <span class="lg-chip" :class="[`tone-${tone}`, { removable }]">
    <button
      v-if="clickable"
      class="lg-chip-body clickable"
      type="button"
      :title="title"
      @click="$emit('click')"
    >
      <slot />
    </button>
    <span v-else class="lg-chip-body"><slot /></span>

    <button
      v-if="removable"
      class="lg-chip-x"
      type="button"
      :aria-label="removeLabel"
      :title="removeLabel"
      @click.stop="$emit('remove')"
    >
      <X :size="13" aria-hidden="true" />
    </button>
  </span>
</template>

<script setup lang="ts">
import { X } from 'lucide-vue-next';

withDefaults(
  defineProps<{
    tone?: 'neutral' | 'accent' | 'info' | 'debug' | 'ok' | 'warn' | 'danger';
    removable?: boolean;
    clickable?: boolean;
    removeLabel?: string;
    title?: string;
  }>(),
  { tone: 'neutral', removable: false, clickable: false, removeLabel: 'Remove' },
);

defineEmits<{ remove: []; click: [] }>();
</script>

<style scoped>
.lg-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--lg-space-1);
  max-width: 100%;
  padding: var(--lg-space-1) var(--lg-space-2);
  border-radius: var(--lg-radius-pill);
  border: 1px solid var(--lg-border-strong);
  border-left-width: 3px;
  border-left-color: var(--tone, var(--lg-border-strong));
  background: var(--lg-bg-surface);
  color: var(--lg-text);
  white-space: nowrap;
}
.lg-chip-body {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  line-height: var(--lg-lh-tight);
  color: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
}
button.lg-chip-body {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
}
.lg-chip-body:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
  border-radius: var(--lg-radius-sm);
}

.lg-chip-x {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-right: -2px;
  padding: 0;
  border: none;
  border-radius: var(--lg-radius-pill);
  background: transparent;
  color: var(--lg-text-dim);
  cursor: pointer;
}
.lg-chip-x:hover {
  color: var(--lg-danger);
  background: var(--lg-bg-hover);
}
.lg-chip-x:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}

/* Same hit-area expansion approach as LgIconButton — keep the visible 18px
   dot but give it a --lg-touch (44px) tap target on mobile. Unlike
   LgIconButton, this expansion is NOT centered: the × sits at the trailing
   edge of the chip, right next to the chip's own clickable body, so a
   centered 44px box would eat ~9-13px into the body's hit area. Instead the
   box only extends 2px past the ×'s own left edge and makes up the target
   size by expanding right/top/bottom, which is safe — nothing sits there. */
@media (max-width: 768px) {
  .lg-chip-x::after {
    content: '';
    position: absolute;
    top: 50%;
    left: -2px;
    right: calc(-1 * ((var(--lg-touch) - 18px) / 2));
    height: var(--lg-touch);
    transform: translateY(-50%);
  }
}

/* tones drive the left-edge accent */
.tone-neutral {
  --tone: var(--lg-border-strong);
}
.tone-accent {
  --tone: var(--lg-accent);
}
.tone-info {
  --tone: var(--lg-info);
}
.tone-debug {
  --tone: var(--lg-level-debug);
}
.tone-ok {
  --tone: var(--lg-ok);
}
.tone-warn {
  --tone: var(--lg-warn);
}
.tone-danger {
  --tone: var(--lg-danger);
}
</style>
